import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router";
import { impersonationActions } from "features/reducers/settings/impersonation.ts";
import { RootState } from "stores/application.store";

/**
 * Enumeration for query parameters used in user emulation.
 *
 * These parameters are used to emulate a specific user's session by providing
 * either their email or account ID in the URL query parameters.
 *
 * @enum {string}
 * @example
 * // Using the enum in code
 * '/new-order/build-order?${EmulationQueryParams.EMAIL}=example@example.com'
 */
export enum EmulationQueryParams {
	EMAIL = "emulatedUserEmail",
	ACCOUNT_ID = "emulatedUserAccountId",
	ACCOUNT_NAME = "emulatedUserAccountName",
	ACCOUNT_NUMBER = "emulatedUserAccountNumber"
}

/**
 * Type for emulation parameters to clearly specify which value is being set.
 * This helps in maintaining type safety when setting emulation parameters.
 */
type EmulationParam = { email?: string; accountId?: string; accountName?: string; accountNumber?: string };

/**
 * A custom React hook that manages user emulation/impersonation via URL query parameters.
 *
 * This hook automatically synchronizes URL search parameters with Redux state for user impersonation
 * and generates URL query strings for the current emulated user.
 *
 * The hook performs two main functions:
 * 1. Synchronizes URL parameters with Redux state (when URL params change, updates Redux)
 * 2. Generates URL query strings for linking to other pages with emulation context
 *
 * @returns URL query string with '?' prefix (e.g., "?emulatedUserEmail=user%40example.com") or empty string
 *
 * @example
 * // Basic usage - generates URL params and handles sync automatically
 * const queryString = useEmulationParams();
 * const url = `/new-order/build-order/123${queryString}`;
 *
 * // The hook automatically handles URL param synchronization in the background
 * // No manual sync calls needed
/**
 *
 *
 * @return {*}  {string}
 */
function useEmulationParams(): string {
	const dispatch = useDispatch();
	const [searchParams, setSearchParams] = useSearchParams();
	const impersonationUser = useSelector((state: RootState) => state.impersonation.impersonationUser);
	const emulatedEmail = searchParams.get(EmulationQueryParams.EMAIL) || "";
	const emulatedAccountId = searchParams.get(EmulationQueryParams.ACCOUNT_ID) || "";
	const emulatedAccountName = searchParams.get(EmulationQueryParams.ACCOUNT_NAME) || "";
	const emulatedAccountNumber = searchParams.get(EmulationQueryParams.ACCOUNT_NUMBER) || "";

	/**
	 * Sets the emulation user based on the provided parameter.
	 * This method updates the impersonation user state in Redux.
	 *
	 * @param {EmulationParam} param - The emulation parameter object containing user information
	 * @returns {void}
	 */
	const setEmulationUser = useCallback(
		(param: EmulationParam) => {
			// Dispatch the parameter object directly since it already has the correct structure
			dispatch(impersonationActions.setImpersonationUser(param));
		},
		[dispatch]
	);

	useEffect(() => {
		// If the impersonation user is set, update the search parameters accordingly
		if (
			(emulatedEmail || emulatedAccountId || emulatedAccountName) &&
			((emulatedEmail && impersonationUser?.email !== emulatedEmail) ||
				(emulatedAccountId && impersonationUser?.accountId !== emulatedAccountId) ||
				(emulatedAccountName && impersonationUser?.accountName !== emulatedAccountName))
		) {
			// Set the emulation user based on the provided parameters
			setEmulationUser({
				...(emulatedAccountId && { accountId: emulatedAccountId }),
				...(emulatedEmail && { email: emulatedEmail }),
				...(emulatedAccountName && { accountName: emulatedAccountName }),
				...(emulatedAccountNumber && { accountNumber: emulatedAccountNumber })
			});
		} else if (impersonationUser) {
			// Clear the emulation search parameters if they exist
			if (
				searchParams.has(EmulationQueryParams.EMAIL) ||
				searchParams.has(EmulationQueryParams.ACCOUNT_ID) ||
				searchParams.has(EmulationQueryParams.ACCOUNT_NAME) ||
				searchParams.has(EmulationQueryParams.ACCOUNT_NUMBER)
			) {
				const newSearchParams = new URLSearchParams(searchParams);
				newSearchParams.delete(EmulationQueryParams.EMAIL);
				newSearchParams.delete(EmulationQueryParams.ACCOUNT_ID);
				newSearchParams.delete(EmulationQueryParams.ACCOUNT_NAME);
				newSearchParams.delete(EmulationQueryParams.ACCOUNT_NUMBER);
				setSearchParams(newSearchParams);
			}
		}
	}, [
		emulatedAccountId,
		emulatedAccountName,
		emulatedAccountNumber,
		emulatedEmail,
		impersonationUser,
		searchParams,
		setEmulationUser,
		setSearchParams
	]);

	if (!impersonationUser?.email && !impersonationUser?.accountId && !impersonationUser?.accountName) {
		return "";
	}

	const urlSearchParams = new URLSearchParams();
	if (impersonationUser.email) {
		urlSearchParams.set(EmulationQueryParams.EMAIL, impersonationUser.email);
	}

	if (impersonationUser.accountId) {
		urlSearchParams.set(EmulationQueryParams.ACCOUNT_ID, impersonationUser.accountId);
	}

	if (impersonationUser.accountName) {
		urlSearchParams.set(EmulationQueryParams.ACCOUNT_NAME, impersonationUser.accountName);
	}

	if (impersonationUser.accountNumber) {
		urlSearchParams.set(EmulationQueryParams.ACCOUNT_NUMBER, impersonationUser.accountNumber);
	}

	// Ensure the query string starts with '?'
	return `?${urlSearchParams.toString()}`;
}

export default useEmulationParams;
